import React, { FC, useCallback, useMemo } from 'react';

import { spacing } from '@rbilabs/universal-components/themes';
import { format } from 'date-fns';
import { Redirect, useRouter } from 'expo-router';
import { ScrollView, StyleSheet, Text, View } from 'react-native';

import { centeredRowContainerStyles } from 'components/layout/centered-row-container';
import { routes } from 'navigation/routing';
import { LoyaltyNoRewardPoints } from 'pages/loyalty/loyalty-no-reward-points';
import { useAuthContext } from 'state/auth';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { primitive } from 'styles/constants/primitives';

import { groupAndSortTransactionsByMonth } from '../transactions/utils';

import { HistoryLazyTableList } from './components/history-lazy-table';
import { HistorySkeleton } from './components/history-skeleton';
import { TransactionTableRow } from './components/types';
import { useLoyaltyHistory } from './hooks/use-loyalty-history';

export const HEADER_IDS: Record<string, TranslationKey> = {
  POINTS: 'perks',
  PURCHASE_AMOUNT: 'purchaseAmount',
};

const getMonthFromTimestamp = (timestamp: string, locale: string): string => {
  try {
    return format(new Date(timestamp), 'MMMM');
  } catch (error) {
    // Fallback in case of error
    return new Date(timestamp).toLocaleDateString(locale, { month: 'long' });
  }
};

export const LoyaltyHistory: FC = () => {
  const { locale } = useLocale();
  const { isAuthenticated } = useAuthContext();

  const router = useRouter();

  const {
    tableData,
    visibleTransactions,
    loadingMore,
    onScroll,
    transactionsLoading,
    loadMoreHistoryTransactions,
  } = useLoyaltyHistory();

  const handleOnPressRow = useCallback(
    (row: TransactionTableRow | Partial<TransactionTableRow>) => {
      if (!row?.rbiOrderId) {
        // if no rbiOrderId, do nothing
        return;
      }
      router.navigate(routes.orderDetails(row.rbiOrderId));
    },
    [router]
  );

  const tables = useMemo(() => {
    if (!tableData.length) {
      return [];
    }

    const groupedTransactions = groupAndSortTransactionsByMonth(tableData);
    return groupedTransactions.map(([, data]) => ({
      head: [
        {
          dateTime: (
            <Text style={[styles.heading, styles.leftHeader]}>
              {getMonthFromTimestamp(data[0].timeStamp, locale)}
            </Text>
          ),
          rewards: <Text style={[styles.heading, styles.leftHeader]}>{t(HEADER_IDS.POINTS)}</Text>,
          purchaseAmount: (
            <Text style={[styles.heading, styles.rightHeader]}>
              {t(HEADER_IDS.PURCHASE_AMOUNT)}
            </Text>
          ),
        },
      ],
      body: data,
    }));
  }, [tableData, locale]);

  if (!isAuthenticated) {
    return <Redirect href={routes.rewardsProfile} />;
  }

  if (transactionsLoading) {
    return (
      <View style={styles.container}>
        <HistorySkeleton />
      </View>
    );
  }

  return (
    <ScrollView
      onScroll={onScroll}
      contentContainerStyle={centeredRowContainerStyles.container}
      style={styles.container}
    >
      <View style={styles.wrapper}>
        {tableData.length ? (
          <>
            <View>
              <Text style={styles.description}>{t('viewYourPurchases')}</Text>
            </View>
            <HistoryLazyTableList
              tables={tables}
              visibleTransactions={visibleTransactions}
              handleOnPressRow={handleOnPressRow}
              loadingMore={loadingMore}
              onLoadMore={loadMoreHistoryTransactions}
            />
          </>
        ) : (
          <LoyaltyNoRewardPoints />
        )}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: primitive.bk.$mayoEggWhite,
    height: '100%',
  },
  wrapper: {
    ...centeredRowContainerStyles.content,
    padding: spacing.$4,
  },
  description: {
    lineHeight: 21,
    textAlign: 'center',
    fontFamily: primitive.fontFamily.$body,
    color: primitive.bk.$bbqBrown,
  },
  heading: {
    fontFamily: primitive.fontFamily.$headers,
    fontSize: primitive.fontSizes.md,
    color: primitive.bk.$bbqBrown,
  },
  leftHeader: {
    textAlign: 'left',
  },
  rightHeader: {
    textAlign: 'right',
  },
});
